/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { useUserId } from '@airasia-rn/account/hooks';
import { useAppConfig } from '@airasia-rn/ui';
import { useLogger } from '@airasia-rn/core-logger';
import { FlightRecommendationsApiService } from '../services/flightRecommendationsApiService';
import { OuterFlightRecommendationsResponse } from '../types/flightRecommendations.types';
import {
  apiErrorMeta,
  classifyServiceError,
  errorTrackingEvent,
} from '../analytics';
import { flightKeys } from './flights';
import { useNearestAirportsCodes } from './useNearestAirportsCodes';
import { useFlightConfig } from '../config/FlightConfigProvider';
import { getApiPlatform } from '../util/utils';

export function useFlightRecommendationsApiService() {
  const { config } = useAppConfig();
  return useMemo(
    () => new FlightRecommendationsApiService(config.env),
    [config.env],
  );
}

export const useFlightRecommendations = (
  lob: string = 'FLIGHT',
  screenName: string = getApiPlatform(),
) => {
  const apiService = useFlightRecommendationsApiService();
  const userIdQuery = useUserId();
  const { config } = useAppConfig();
  const { config: flightConfig } = useFlightConfig();
  const { logError } = useLogger();
  const { data: nearestAirportsData, isSuccess: nearestAirportSuccess } =
    useNearestAirportsCodes(screenName);

  const payload = {
    userId: userIdQuery.data ?? 'flightsios', // OTA_USERNAME,
    lob: [lob],
    debug: false,
    locale: config.locale ?? 'en-gb',
    location: {
      nearestAirport: nearestAirportsData?.airports?.[0]?.stationCode,
    },
    currency: flightConfig?.currency || config.currency,
  };

  const query = useQuery<OuterFlightRecommendationsResponse, Error>({
    queryKey: [...flightKeys.recommendations, payload],
    enabled: nearestAirportSuccess,

    queryFn: ({ signal }) => {
      return apiService.fetchFlightRecommendations(payload, signal);
    },
  });

  // react-query v5 dropped useQuery's onError callback, so error tracking
  // has to observe the query result instead of hooking into the query itself.
  useEffect(() => {
    if (!query.isError) return;

    const { errorType, errorCode, errorDescription, errorMessage } =
      classifyServiceError(query.error);

    logError(
      apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
      errorTrackingEvent({
        errorType,
        errorCode,
        errorDescription,
        errorMessage,
        screenName,
        service: apiErrorMeta.SERVICE_NAMES.FLIGHT_RECOMMENDATIONS,
      }),
    );
  }, [query.isError, query.error, screenName, logError]);

  return query;
};
